import React, { useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { PhoneCountrySelect } from "../../../components/common/u1/PhoneCountrySelect";
import "react-phone-number-input/style.css";

import {
  useSignUpEmail,
  useSignUpVerification,
  useSignUpProfile,
} from "../../../Hooks/u1/useSignUpFlow";
import { validateEmail } from "../../../utils/Helper";
import ClaimCompanyU1 from "./ClaimCompany";

import { Navbar } from "../../../components/common/u1/Navbar";
import { AuthCard } from "../../../components/common/u1/AuthCard";
import { AuthHeader } from "../../../components/common/u1/AuthHeader";
import { StepRail } from "../../../components/common/u1/StepRail";
import { OtpInput } from "../../../components/common/u1/OtpInput";
import { PasswordRequirements } from "../../../components/common/u1/PasswordRequirements";
import { isPasswordValid } from "../../../components/common/u1/passwordRules";
import { Input } from "../../../components/common/u1/Input";
import { PasswordInput } from "../../../components/common/u1/PasswordInput";
import { Button } from "../../../components/common/u1/Button";
import { ErrorNote } from "../../../components/common/u1/ErrorNote";
import { ChoiceCard } from "../../../components/common/u1/ChoiceCard";
import { SuccessMark } from "../../../components/common/u1/SuccessMark";
import {
  ArrowRight,
  CheckIcon,
  BuildingIcon,
  GridIcon,
} from "../../../assets/ui/icons";
import "../../../components/common/u1/tokens.css";

/* u1 Sign Up — the first three steps of registration. */

const NavActions = () => (
  <Link to="/sign-up">
    <Button variant="primary" className="whitespace-nowrap">
      List your business <ArrowRight size={14} />
    </Button>
  </Link>
);

/* ── Step 1 — work email ─────────────────────────────────────────────────── */
const EmailStep = ({ defaultEmail = "", onNext }) => {
  const { submit, error, isLoading } = useSignUpEmail({ onNext });
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: { email: defaultEmail || "" },
  });

  return (
    <form onSubmit={handleSubmit((v) => submit(v.email))} className="w-full">
      <AuthHeader
        title="Create your account"
        subtitle="Enter your work email — we'll send you a 6-digit code."
      />
      <StepRail current={1} total={3} />

      <div className="mt-6">
        <Input
          id="signup-email"
          size="lg"
          type="email"
          label="Work email"
          autoComplete="email"
          placeholder="you@company.com"
          error={errors.email?.message}
          {...register("email", {
            required: "Enter your work email",
            validate: (v) =>
              Boolean(validateEmail(v)) || "Invalid Email, Please enter a valid email",
          })}
        />
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      <Button type="submit" disabled={!isValid || isLoading} className="w-full mt-5 h-[46px]">
        {isLoading ? "Sending…" : "Send code"}
        {!isLoading && <ArrowRight size={14} />}
      </Button>

      <p className="mt-5 text-center text-[13px] text-[var(--ink-2)] font-sans">
        Already on TREVEX?{" "}
        <Link to="/login" className="text-[var(--orange)] font-semibold hover:underline">
          Log in instead
        </Link>
      </p>
    </form>
  );
};

/* ── Step 2 — verify the code ────────────────────────────────────────────── */
const VerifyStep = ({ email, verificationCode, onNext, onBack, onResent }) => {
  const [otp, setOtp] = useState("");
  const { submit, resend, error, isLoading, resending, resent } = useSignUpVerification({
    email,
    verificationCode,
    onNext,
    /* A resent code is only valid against the new challenge, so the shell
       swaps it in. The digits already typed belong to the dead code, so they
       are cleared rather than left to fail on submit. */
    onResent: (encrypted) => {
      onResent(encrypted);
      setOtp("");
    },
  });
  const complete = otp.length === 6;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (complete) submit(otp);
      }}
      className="w-full"
    >
      <AuthHeader
        title="Create your account"
        subtitle={`We sent a code to ${email}. Enter it below.`}
      />
      <StepRail current={2} total={3} />

      <div className="mt-6">
        <OtpInput value={otp} onChange={setOtp} disabled={isLoading} />
      </div>

      <p className="mt-4 text-center text-[13px] text-[var(--ink-2)] font-sans">
        Didn&apos;t get it?{" "}
        <button
          type="button"
          onClick={resend}
          disabled={resending}
          className="text-[var(--orange)] font-semibold hover:underline disabled:opacity-60 disabled:cursor-not-allowed disabled:no-underline"
        >
          {resending ? "Sending…" : "Resend code"}
        </button>
        {/* Confirmation sits on the same row as the link. A polite live region
            so it is announced without stealing focus from the code boxes. */}
        {resent && !error && (
          <span
            role="status"
            className="inline-flex items-center gap-1 ml-2 text-[var(--success)] font-semibold align-middle"
          >
            Sent
            <CheckIcon size={13} stroke="var(--success)" />
          </span>
        )}
      </p>

      {error && <ErrorNote>{error}</ErrorNote>}

      <div className="mt-6 flex items-center gap-2 sm:gap-3">
        <Button type="button" variant="ghost" onClick={onBack} className="h-[52px] px-6 min-w-[132px] shrink-0">
          Back
        </Button>
        <Button type="submit" disabled={!complete || isLoading} className="flex-1 w-[100%] h-[52px]">
          {isLoading ? "Verifying…" : "Verify"}
          {!isLoading && <ArrowRight size={14} />}
        </Button>
      </div>
    </form>
  );
};

/* ── Step 3 — profile + password ─────────────────────────────────────────── */
const ProfileStep = ({ email, isArticle, slug, onNext, onBack }) => {
  /* The success screen greets the new account by name. */
  const submittedName = useRef("");
  const { submit, error, isLoading } = useSignUpProfile({
    email,
    isArticle,
    slug,
    onNext: (created) => onNext(created, submittedName.current),
  });
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      designation: "",
      employer_company_name: "",
      phone: "",
      password: "",
    },
  });

  const password = watch("password");

  return (
    <form
      onSubmit={handleSubmit((values) => {
        submittedName.current = values.name;
        return submit(values);
      })}
      className="w-full"
    >
      <AuthHeader
        title="Create your account"
        subtitle="A few details, then a password, and you're in."
      />
      <StepRail current={3} total={3} />

      <div className="grid gap-3.5 mt-6">
        <Input
          id="signup-name"
          size="lg"
          label="Full name"
          autoComplete="name"
          placeholder="Your name"
          error={errors.name?.message}
          {...register("name", { required: "Enter your full name" })}
        />

        <Input
          id="signup-designation"
          size="lg"
          label="Designation"
          placeholder="e.g. Credit Manager"
          error={errors.designation?.message}
          {...register("designation", { required: "Enter your designation" })}
        />

        <Input
          id="signup-company"
          size="lg"
          label="Company name"
          autoComplete="organization"
          placeholder="Your company"
          error={errors.employer_company_name?.message}
          {...register("employer_company_name", { required: "Enter your company name" })}
        />

        {/* Transcribed rule: the contact number is checked with
            react-phone-number-input's isValidPhoneNumber, as the existing
            screen does — not a hand-written pattern. */}
        <Controller
          name="phone"
          control={control}
          rules={{
            required: "Enter your contact number",
            validate: (v) =>
              isValidPhoneNumber(String(v || "")) || "Enter a valid contact number",
          }}
          render={({ field }) => (
            <div className="flex flex-col gap-2">
              <label
                htmlFor="signup-phone"
                className="text-[13px] font-semibold text-[var(--ink)] tracking-[0.005em] font-sans"
              >
                Contact number
              </label>
              <PhoneInput
                id="signup-phone"
                international
                defaultCountry="AE"
                placeholder="+971 …"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                /* The app's own country menu instead of the library's bare <select>: the same searchable list, GCC markets first, that every other uplifted field uses. */
                countrySelectComponent={PhoneCountrySelect}
                className={`u1-phone h-12 md:h-[52px] w-full rounded-[10px] border-[1.5px] px-4 bg-white transition-[border-color,box-shadow] duration-150 ${
                  errors.phone
                    ? "border-[#D61E1E]"
                    : "border-[var(--line-2)] hover:border-[var(--muted-2)]"
                }`}
              />
              {errors.phone && (
                <p role="alert" className="text-[12px] text-[#D61E1E] font-sans">
                  {errors.phone.message}
                </p>
              )}
            </div>
          )}
        />

        <div>
          <PasswordInput
            id="signup-password"
            size="lg"
            label="Password"
            autoComplete="new-password"
            placeholder="Choose a strong password"
            visible={showPassword}
            onToggleVisibility={() => setShowPassword((v) => !v)}
            error={errors.password?.message}
            {...register("password", {
              required: "Choose a password",
              validate: (v) => isPasswordValid(v) || "Password does not meet the requirements",
            })}
          />
          <PasswordRequirements value={password} />
        </div>
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      <div className="mt-6 flex items-center gap-2 sm:gap-3">
        <Button type="button" variant="ghost" onClick={onBack} className="h-[52px] px-6 min-w-[132px] shrink-0">
          Back
        </Button>
        <Button type="submit" disabled={!isValid || isLoading} className="flex-1 w-[100%] h-[52px]">
          {isLoading ? "Creating…" : "Create account"}
          {!isLoading && <ArrowRight size={14} />}
        </Button>
      </div>
    </form>
  );
};

/* ── Step 4 — account created, pick a starting point ─────────────────────── */
const SuccessStep = ({ name = "", onListBusiness, onDashboard }) => {
  // Just the given name: "You're in, Priya." reads better than the full legal
  // name the form collects. Falls back to the plain greeting if it is missing.
  const firstName = String(name || "").trim().split(/\s+/)[0] || "";

  return (
    <div className="w-full">
      <AuthHeader
        title="Create your account"
        subtitle="You're all set."
      />
      {/* All three steps are behind us, so the rail reads complete. */}
      <StepRail current={3} total={3} />
      <div className="flex flex-col mt-6 items-center text-center">
        <SuccessMark />
        <h1 className="mt-5 text-[26px] sm:text-[30px] leading-[1.15] font-bold tracking-[-0.02em] text-[var(--ink)] font-sans">
          {firstName ? `You're in, ${firstName}.` : "You're in."}
        </h1>
        <p className="mt-2 text-[14px] leading-[1.5] text-[var(--ink-2)] font-sans">
          Your TREVEX account is active. Where would you like to start?
        </p>
      </div>

      <div className="grid gap-3 mt-6">
        <ChoiceCard
          variant="recommended"
          icon={<BuildingIcon />}
          title="List my business"
          description="Find your company in the UAE directory and claim your listing."
          onClick={onListBusiness}
        />
        <ChoiceCard
          icon={<GridIcon />}
          title="Go to my dashboard"
          description="Browse TREVEX, follow profiles, and order Web Profiles."
          onClick={onDashboard}
        />
      </div>
    </div>
  );
};

/* ── The four-step shell ─────────────────────────────────────────────────── */
export default function SignUp({ initialEmail = "", onComplete = () => {} }) {
  const routerLocation = useLocation();
  const { search } = routerLocation;
  const params = new URLSearchParams(search);
  const isArticle = params.get("app") === "article";
  const slug = params.get("slug");

  const navigate = useNavigate();

  /* TIP-629 — the company-search entry point. `redirectToStep: 6` is the
     convention every "List your business" call site already carries, and the
     one Login replays after sign-in; step 6 of the original orchestrator was
     SearchCompany. It opens the claim intake, whose first step does the
     directory search, rather than the registration steps below. */
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");

  if (routerLocation?.state?.redirectToStep === 6) {
    return <ClaimCompanyU1 />;
  }

  return (
    <div className="u1-root min-h-screen w-full bg-[var(--bg)] flex flex-col">
      <Navbar actions={<NavActions />} />

      <AuthCard>
        {step === 1 && (
          <EmailStep
            defaultEmail={email}
            onNext={(nextEmail, encrypted) => {
              setEmail(nextEmail);
              setCode(encrypted);
              setStep(2);
            }}
          />
        )}

        {step === 2 && (
          <VerifyStep
            email={email}
            verificationCode={code}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
            onResent={setCode}
          />
        )}

        {step === 3 && (
          <ProfileStep
            email={email}
            isArticle={isArticle}
            slug={slug}
            onBack={() => setStep(2)}
            /* `onComplete` still fires exactly as before, so a caller that
               routes the flow onward keeps doing so. When nobody claims it —
               the default — step 4 offers the two starting points. */
            onNext={(created, fullName) => {
              setName(fullName || "");
              setStep(4);
              onComplete(created);
            }}
          />
        )}

        {/* Step 3 registers and then signs the new account in, so both
            destinations are ordinary navigations — /dashboard is behind
            PrivateRoute and the session token is already in the store. */}
        {step === 4 && (
          <SuccessStep
            name={name}
            onListBusiness={() => navigate("/search")}
            onDashboard={() => navigate("/dashboard")}
          />
        )}
      </AuthCard>
    </div>
  );
}
